import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CheckCircle, Shield, Lock, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, t, API } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const assessment = location.state?.assessment;
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  if (!assessment) {
    navigate('/');
    return null;
  }

  const { score, is_compromised, id } = assessment;
  const isCompromised = is_compromised || score >= 2;

  const handleBookAppointment = () => {
    if (settings?.other_device_flow === 'booking') {
      // Go directly to booking
      navigate('/book-appointment', { state: { assessment } });
    } else {
      // Go to payment page (default)
      navigate('/payment', { state: { assessment } });
    }
  };

  return (
    <div className="min-h-screen p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="max-w-5xl mx-auto py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('yourResults', language)}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className={`glass-panel rounded-sm p-8 md:p-12 ${
              isCompromised ? 'border-2 border-secondary danger-glow' : 'border-2 border-primary neon-glow'
            }`}>
              <div className="flex items-start gap-6 mb-6">
                {isCompromised ? (
                  <AlertTriangle className="w-16 h-16 text-secondary flex-shrink-0" data-testid="compromised-icon" />
                ) : (
                  <CheckCircle className="w-16 h-16 text-primary flex-shrink-0" data-testid="secure-icon" />
                )}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-2">
                    {isCompromised ? t('deviceCompromised', language) : t('deviceSecure', language)}
                  </h2>
                  <p className="text-4xl font-mono text-muted-foreground">
                    {score} {t('score', language)}
                  </p>
                </div>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                {isCompromised ? t('compromisedDesc', language) : t('secureDesc', language)}
              </p>
            </div>

            <div className="glass-panel rounded-sm p-8 md:p-12">
              <h3 className="text-xl md:text-2xl font-semibold mb-4 flex items-center gap-3">
                <Shield className="w-8 h-8 text-primary" />
                {t('ourService', language)}
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">{t('serviceDesc', language)}</p>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-sm border border-primary/20">
                  <Lock className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <p className="text-sm">{t('privacy', language)}</p>
                </div>
                <div className="flex items-start gap-3 p-3 bg-primary/5 rounded-sm border border-primary/20">
                  <Info className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                  <p className="text-sm">{t('duration', language)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="glass-panel rounded-sm p-8 border-2 border-premium premium-glow sticky top-6">
              <div className="text-center mb-6">
                <p className="text-sm font-mono uppercase tracking-widest text-premium mb-2">
                  {t('consultationFee', language)}
                </p>
                <p className="text-5xl font-bold text-premium" data-testid="consultation-price">€ 400</p>
              </div>

              <Button
                onClick={handleBookAppointment}
                data-testid="book-appointment-button"
                className="w-full h-14 rounded-none border border-premium bg-premium/10 text-premium hover:bg-premium hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm mb-4"
              >
                {t('bookAppointment', language)}
              </Button>

              <Button
                variant="outline"
                onClick={() => navigate('/')}
                data-testid="start-new-assessment-button"
                className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono text-xs"
              >
                New Assessment
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
